import { Text, View, StyleSheet } from '@react-pdf/renderer';
import moment from 'moment';
import { formatCurrency } from '../../lib/util';
import PDFPage from './PDFDocument';
import { colors } from '../../lib/styles';

const H2_FONT_SIZE = 12;
const H3_FONT_SIZE = 9;
const VALUE_FONT_SIZE = 8;

const styles = StyleSheet.create({
  container: {
    padding: 10,
  },
  sectionHeader: {
    backgroundColor: colors.primaryColor,
    color: 'white',
    padding: 4,
    fontWeight: 'bold',
    fontSize: H2_FONT_SIZE,
    textAlign: 'center',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  sectionContent: {
    padding: 10,
    backgroundColor: 'white',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    marginBottom: 10,
  },
  fieldGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 2,
  },
  fieldItem: {
    flex: 1,
    minWidth: 50,
  },
  fieldLabel: {
    fontSize: H3_FONT_SIZE,
    fontWeight: 'bold',
    marginBottom: 2,
    color: colors.primaryColor,
  },
  fieldValue: {
    fontSize: VALUE_FONT_SIZE,
    padding: 4,
    minHeight: 16,
    marginBottom: 5,
  },
  dataSection: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  taxBucketSection: {
    width: '32%',
    minWidth: 180,
    marginBottom: 10,
  },
  taxBucketTitle: {
    backgroundColor: colors.primaryColor,
    color: 'white',
    fontWeight: 'bold',
    fontSize: H2_FONT_SIZE,
    textAlign: 'center',
    padding: 4,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  taxBucketContent: {
    padding: 4,
    backgroundColor: 'white',
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    borderStyle: 'solid',
  },
  taxBucketHeaderRow: {
    flexDirection: 'row',
  },
  taxBucketHeader: {
    flex: 1,
    fontWeight: 'bold',
    fontSize: H3_FONT_SIZE,
    padding: 4,
    textAlign: 'center',
  },
  taxBucketRow: {
    flexDirection: 'row',
    marginBottom: 2,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    padding: 4,
  },
  taxBucketTotalRow: {
    flexDirection: 'row',
    marginTop: 4,
    padding: 2,
  },
  taxBucketCell: {
    flex: 1,
    fontSize: VALUE_FONT_SIZE,
  },
  taxBucketCellCurrency: {
    flex: 1,
    fontSize: VALUE_FONT_SIZE,
    textAlign: 'right',
  },
  noteSection: {
  fontSize: VALUE_FONT_SIZE,
},
});

const TaxBucketSection = ({ title, assets }) => {
  if (assets.length === 0) return null;

  return (
    <View style={styles.taxBucketSection}>
      <Text style={styles.sectionHeader}>{title}</Text>

      <View style={styles.taxBucketContent}>
        {/* Header Row */}
        <View style={styles.taxBucketHeaderRow}>
          <Text style={styles.taxBucketHeader}>Account Type</Text>
          <Text style={styles.taxBucketHeader}>Location / Note</Text>
          <Text style={styles.taxBucketHeader}>Amount</Text>
        </View>

        {/* Data Rows */}
        {assets.map((asset, index) => (
          <View key={index} style={styles.taxBucketRow}>
            <Text style={styles.taxBucketCell}>{asset.account_type || ''}</Text>
            <Text style={styles.taxBucketCell}>{asset.notes || ''}</Text>
            <Text style={styles.taxBucketCellCurrency}>${formatCurrency(asset.value || 0)}</Text>
          </View>
        ))}
        <View style={styles.taxBucketTotalRow}>
          <Text style={styles.taxBucketCell}></Text>
          <Text style={{ ...styles.taxBucketCell, fontWeight: 'bold' }}>Total</Text>
          <Text style={{ ...styles.taxBucketCellCurrency, fontWeight: 'bold' }}>${formatCurrency(assets.reduce((sum, a) => sum + (Number(a.value) || 0), 0))}</Text>
        </View>
      </View>
    </View>
  );
};

const ClientProfilePDF = ({ clientData, pageNumber }) => {
  if (!clientData) {
    console.error('ClientProfilePDF: No client data provided');
    return null;
  }

  const displayName = `${clientData?.firstname || ''} ${clientData?.lastname || ''}`;
  const spouseDisplayName = `${clientData?.spouse_firstname || ''} ${clientData?.spouse_lastname || ''}`;

  const filteredAssets = Array.isArray(clientData?.assets) ? clientData.assets : [];

  return (
    <PDFPage
      header="Client Profile"
      pageNumber={pageNumber}
      showDisclaimer={true}
      showFooter={true}
    >
      <View style={styles.container}>
        {/* Primary Client Information */}
        <View>
          <Text style={styles.sectionHeader}>Primary Client Information</Text>
          <View style={styles.sectionContent}>
            <View style={styles.fieldGrid}>
              <View style={styles.fieldItem}>
                <Text style={styles.fieldLabel}>Name</Text>
                <Text style={styles.fieldValue}>{displayName}</Text>
                <Text style={styles.fieldValue}>{spouseDisplayName}</Text>
              </View>
              <View style={styles.fieldItem}>
                <Text style={styles.fieldLabel}>Date of Birth</Text>
                <Text style={styles.fieldValue}>{clientData.dob ? moment(clientData.dob).format('MM/DD/YYYY') : ''}</Text>
                <Text style={styles.fieldValue}>{clientData.spouse_dob ? moment(clientData.spouse_dob).format('MM/DD/YYYY') : ''}</Text>
              </View>
              <View style={styles.fieldItem}>
                <Text style={styles.fieldLabel}>Current Age</Text>
                <Text style={styles.fieldValue}>{clientData.age || ''}</Text>
                <Text style={styles.fieldValue}>{clientData.spouseAge || ''}</Text>
              </View>
              <View style={styles.fieldItem}>
                <Text style={styles.fieldLabel}>Current Income</Text>
                <Text style={styles.fieldValue}>${clientData.current_income ? formatCurrency(clientData.current_income) : ''}</Text>
                <Text style={styles.fieldValue}>${clientData.spouse_current_income ? formatCurrency(clientData.spouse_current_income) : ''}</Text>
              </View>
              <View style={styles.fieldItem}>
                <Text style={styles.fieldLabel}>Yrs to Retire</Text>
                <Text style={styles.fieldValue}>{clientData.yearsToRetirement}</Text>
                <Text style={styles.fieldValue}>{clientData.spouseYearsToRetirement}</Text>
              </View>
              <View style={styles.fieldItem}>
                <Text style={styles.fieldLabel}>Desired Annual Target Income (ATI)</Text>
                <Text style={styles.fieldValue}>${clientData.desired_ati ? formatCurrency(clientData.desired_ati) : ''}</Text>
              </View>
               <View style={styles.fieldItem}>
                <Text style={styles.fieldLabel}>Pension</Text>
                <Text style={styles.fieldValue}>${clientData.pension ? formatCurrency(clientData.pension) : ''}</Text>
                <Text style={styles.fieldValue}>${clientData.spouse_pension ? formatCurrency(clientData.spouse_pension) : ''}</Text>
              </View>
              <View style={styles.fieldItem}>
                <Text style={styles.fieldLabel}>Rental</Text>
                <Text style={styles.fieldValue}>${clientData.rental ? formatCurrency(clientData.rental) : ''}</Text>
                <Text style={styles.fieldValue}>${clientData.spouse_rental ? formatCurrency(clientData.spouse_rental) : ''}</Text>
              </View>
              <View style={styles.fieldItem}>
                <Text style={styles.fieldLabel}>Soc. Security</Text>
                <Text style={styles.fieldValue}>${clientData.social_security ? formatCurrency(clientData.social_security) : ''}</Text>
                <Text style={styles.fieldValue}>${clientData.spouse_social_security ? formatCurrency(clientData.spouse_social_security) : ''}</Text>
              </View>
              <View style={styles.fieldItem}>
                <Text style={styles.fieldLabel}>Ret Contrib.</Text>
                <Text style={styles.fieldValue}>${clientData.ret_contributions ? formatCurrency(clientData.ret_contributions) : ''}</Text>
                <Text style={styles.fieldValue}>${clientData.spouse_ret_contributions ? formatCurrency(clientData.spouse_ret_contributions) : ''}</Text>
              </View>
              <View style={styles.fieldItem}>
                <Text style={styles.fieldLabel}>Filing Status</Text>
                <Text style={styles.fieldValue}>{clientData.filing_status || ''}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Tax Buckets */}
        <View style={styles.dataSection}>
          <TaxBucketSection
            title="Non-Qualified"
            assets={filteredAssets.filter(a => a?.asset_type === "NON_QUALIFIED")}
          />
          
          <TaxBucketSection
            title="Tax Deferred"
            assets={filteredAssets.filter(a => a?.asset_type === "TAX_DEFFERED")}
          />
          
          <TaxBucketSection
            title="Tax Free"
            assets={filteredAssets.filter(a => a?.asset_type === "TAX_FREE")}
          />

          {/* Miscellaneous Assets */}
          <View style={styles.taxBucketSection}>
            <Text style={styles.taxBucketTitle}>Misc</Text>
            <View style={styles.taxBucketContent}>
              <View style={styles.taxBucketRow}>
                <Text style={styles.taxBucketCell}>LTC</Text>
                <Text style={styles.taxBucketCell}>Lorem ipsum</Text>
              </View>
              <View style={styles.taxBucketRow}>
                <Text style={styles.taxBucketCell}>Legacy</Text>
                <Text style={styles.taxBucketCell}>Lorem ipsum</Text>
              </View>
              <View style={styles.taxBucketRow}>
                <Text style={styles.taxBucketCell}>Military / 1st Resp</Text>
                <Text style={styles.taxBucketCell}>Lorem ipsum</Text>
              </View>
              <View style={styles.taxBucketRow}>
                <Text style={styles.taxBucketCell}>Home Value</Text>
                <Text style={styles.taxBucketCell}>$100</Text>
              </View>
              <View style={styles.taxBucketRow}>
                <Text style={styles.taxBucketCell}>Home Mortgage</Text>
                <Text style={styles.taxBucketCell}>$100</Text>
              </View>
              <View style={styles.taxBucketRow}>
                <Text style={styles.taxBucketCell}>Home Equity</Text>
                <Text style={styles.taxBucketCell}>$100</Text>
              </View>
            </View>
          </View>

          {/* Total Retirement Assets */}
          <View style={styles.taxBucketSection}>
            <Text style={styles.taxBucketTitle}>Total Retirement Assets</Text>
            <View style={styles.taxBucketContent}>
              <View style={styles.taxBucketTotalRow}>
                <Text style={styles.taxBucketHeader}>
                  ${formatCurrency(clientData.totalRetAssets || 0)}
                </Text>
              </View>
            </View>
          </View>

          {/* Notes */}
          <View style={styles.taxBucketSection}>
            <Text style={styles.taxBucketTitle}>Notes</Text>
            <View style={styles.taxBucketContent}>
              <Text style={styles.noteSection}>{clientData.notes || 'No notes provided.'}</Text>
            </View>
          </View>
        </View>
      </View>
    </PDFPage>
  );
};

export default ClientProfilePDF;